import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { STATUS_LABEL, STATUS_COLOR, isPublished, won } from '../utils/versionUtils';

const MAX_PANES = 4;
const MIN_PCT = 15;

// 같은 견적의 여러 버전을 한 화면에 나란히 보여주는 분할 보기 (iframe). ?ids=1,2,3&bt=goods|manpower
export default function EstimateSideBySidePage() {
  const [sp] = useSearchParams();
  const ids = (sp.get('ids') || '').split(',').map(Number).filter(Boolean).slice(0, MAX_PANES);
  const base = sp.get('bt') === 'manpower' ? '/service-estimate/new' : '/product-estimate/new';
  const [meta, setMeta] = useState({});
  const [ws, setWs] = useState(() => ids.map(() => 100 / ids.length));
  const [drag, setDrag] = useState(null); // { i, startX, startWs }
  const wrapRef = useRef(null);

  useEffect(() => {
    document.title = '견적 버전 나란히 보기';
    Promise.all(ids.map(id => api.get(`/estimates/${id}`).then(e => [id, e]).catch(() => [id, null])))
      .then(rows => setMeta(Object.fromEntries(rows)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 구분선 드래그로 인접한 두 칸의 폭 조절
  useEffect(() => {
    if (!drag) return undefined;
    const move = (e) => {
      const W = wrapRef.current.getBoundingClientRect().width;
      const pair = drag.startWs[drag.i] + drag.startWs[drag.i + 1];
      const a = Math.max(MIN_PCT, Math.min(pair - MIN_PCT, drag.startWs[drag.i] + ((e.clientX - drag.startX) / W) * 100));
      const next = [...drag.startWs];
      next[drag.i] = a;
      next[drag.i + 1] = pair - a;
      setWs(next);
    };
    const up = () => setDrag(null);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [drag]);

  if (ids.length < 2) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>나란히 볼 버전을 2개 이상 선택해 주세요. (목록에서 버전을 체크한 뒤 [나란히 보기])</div>;
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f1f5f9' }}>
      <div style={{ padding: '8px 16px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontWeight: 800, fontSize: 14 }}>▥ 버전 나란히 보기</span>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>가운데 구분선을 드래그해 폭을 조절할 수 있습니다</span>
        <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setWs(ids.map(() => 100 / ids.length))}>균등 분할</button>
        <button className="btn btn-outline btn-sm" onClick={() => window.close()}>닫기</button>
      </div>
      <div ref={wrapRef} style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {ids.map((id, i) => {
          const e = meta[id];
          return (
            <div key={id} style={{ display: 'flex', flex: 'none', width: `${ws[i]}%`, minWidth: 0 }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: '#fff' }}>
                <div style={{ padding: '6px 12px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  <b style={{ fontSize: 14 }}>{e ? `v${e.version}` : `#${id}`}</b>
                  {e && <span style={{ color: STATUS_COLOR[e.status] }}>● {STATUS_LABEL[e.status] || e.status}{isPublished(e.status) && ' ✔'}</span>}
                  {e && <span style={{ fontWeight: 700 }}>{won(e.total_amount)}</span>}
                  {e?.version_note && <span title={e.version_note} style={{ color: '#64748b', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis' }}>“{e.version_note}”</span>}
                  <a href={`${base}?id=${id}`} target="_blank" rel="noreferrer" style={{ marginLeft: 'auto', color: '#3b82f6', fontSize: 11 }}>새 탭 ↗</a>
                </div>
                <iframe title={`estimate-${id}`} src={`${base}?id=${id}`}
                  style={{ flex: 1, border: 0, width: '100%', pointerEvents: drag ? 'none' : 'auto' }} />
              </div>
              {i < ids.length - 1 && (
                <div onMouseDown={(ev) => { ev.preventDefault(); setDrag({ i, startX: ev.clientX, startWs: ws }); }}
                  title="드래그해서 폭 조절"
                  style={{ width: 6, cursor: 'col-resize', background: drag?.i === i ? '#3b82f6' : '#cbd5e1', flex: 'none' }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
